import { createBrowserRouter, Navigate, Outlet, useParams } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import PublicLegalPageLayout from '../layouts/PublicLegalPageLayout';
import AppLayout from '../layouts/AppLayout';
import InternalAdminLayout from '../layouts/InternalAdminLayout';
import LoadingBlock from '../components/LoadingBlock';
import useAuth from '../hooks/useAuth';
import useInternalAdminAuth from '../contexts/InternalAdminAuthContext';

import LoginPage from '../pages/auth/LoginPage';
import ForgotPasswordPage from '../pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '../pages/auth/ResetPasswordPage';
import SignupStartPage from '../pages/signup/SignupStartPage';
import SignupOtpPage from '../pages/signup/SignupOtpPage';
import SignupPaymentPage from '../pages/signup/SignupPaymentPage';
import SignupSezPendingPage from '../pages/signup/SignupSezPendingPage';
import SignupDonePage from '../pages/signup/SignupDonePage';

// Stage 5N-2B: internal admin pages.
import InternalAdminLoginPage from '../pages/internalAdmin/InternalAdminLoginPage';
import PendingAccountsPage from '../pages/internalAdmin/PendingAccountsPage';
import SezPendingSignupsPage from '../pages/internalAdmin/SezPendingSignupsPage';
import AccountDetailPage from '../pages/internalAdmin/AccountDetailPage';
// Stage 5N-2C: additional internal admin pages.
import ApprovedAccountsPage from '../pages/internalAdmin/ApprovedAccountsPage';
import RejectedAccountsPage from '../pages/internalAdmin/RejectedAccountsPage';
import SuspendedAccountsPage from '../pages/internalAdmin/SuspendedAccountsPage';
import AuditLogsPage from '../pages/internalAdmin/AuditLogsPage';
import AdminProfilePage from '../pages/internalAdmin/AdminProfilePage';
// Stage 5N-3: Custom Plan management pages.
import CustomPlansListPage from '../pages/internalAdmin/CustomPlansListPage';
import CustomPlanFormPage from '../pages/internalAdmin/CustomPlanFormPage';
import CustomPlanDetailPage from '../pages/internalAdmin/CustomPlanDetailPage';
// Stage 5N-4: Pricing & Tax page.
import PricingPage from '../pages/internalAdmin/PricingPage';
import TenantConfigClonePage from '../pages/internalAdmin/TenantConfigClonePage';
// Stage SCAN-1: ESP32 scanner hardware inventory + tenant allocation.
import ScanDeviceInventoryPage from '../pages/internalAdmin/ScanDeviceInventoryPage';
import ReportDefinitionsPage from '../pages/internalAdmin/ReportDefinitionsPage';
// /signup/quote was deprecated — plan selection now lives inline on
// /signup/payment. The route below remains as a redirect so that any
// bookmarks, email links, or in-flight tabs land on the new flow safely
// instead of a 404. SignupQuotePage.jsx is no longer imported.

import PrivacyPolicyPage from '../pages/legal/PrivacyPolicyPage';
import TermsOfServicePage from '../pages/legal/TermsOfServicePage';
import RefundPolicyPage from '../pages/legal/RefundPolicyPage';
import ContactPage from '../pages/legal/ContactPage';

import InvoiceListPage from '../pages/invoices/InvoiceListPage';
import InvoiceDetailPage from '../pages/invoices/InvoiceDetailPage';
import UserListPage from '../pages/users/UserListPage';
import MyProfilePage from '../pages/users/MyProfilePage';
import SessionDebugPage from '../pages/session/SessionDebugPage';
import SubscriptionPage from '../pages/subscription/SubscriptionPage';
import RenewSubscriptionPage from '../pages/subscription/RenewSubscriptionPage';
// Stage 5N-9A: in-app Jasper report viewer (internal-admin only).
// The tenant-side InvoiceReportViewerPage was removed — every
// tenant invoice view now goes through the reusable report engine.
import InternalInvoiceReportViewerPage from '../pages/internalAdmin/InternalInvoiceReportViewerPage';
// Stage R3 viewer rework — the report engine no longer exposes a
// job-history surface to end users. Every report click creates a
// fresh temporary job and routes the user straight to a
// Crystal-Reports-style viewer at /reports/viewer/:jobId. The
// legacy ReportJobsPage / ReportRequestPage are no longer mounted;
// the legacy /reports/jobs/:id path is kept as a one-way redirect
// to the new viewer so any in-flight tabs converge on it.
import ReportEngineViewerPage from '../pages/reports/ReportEngineViewerPage';

// Stage 6A foundation — isolated sandbox for the reusable ErpDataGrid.
// Local-state only; no API, DB, or workflow impact. Mounted under the
// regular ProtectedRoute → AppLayout chrome so the demo inherits the
// real sidebar / topbar. Safe to remove by deleting:
//   - this import line
//   - the `/dev/erp-grid-demo` route entry below
//   - the optional Sidebar.jsx link (if added)
//   - the entire `frontend/src/components/common/erp-grid/` folder
//   - the demo page file `frontend/src/pages/dev/ErpGridDemoPage.jsx`
import ErpGridDemoPage from '../pages/dev/ErpGridDemoPage';
// Stage 6A Grid Increment 3 — analytical surfaces (nested filter UI,
// server-meta group summaries, pivot preview). Separate dev page so
// the operational `/dev/erp-grid-demo` stays a clean Master template.
import ErpGridBiDemoPage from '../pages/dev/ErpGridBiDemoPage';

// Stage 6A.2 — Customer Master (first production ERP module). Lives
// under /erp/masters/customers and is backed by the tenant-schema
// customers table created by tenant migration 0001.
import CustomerMasterPage from '../pages/erp/masters/customers/CustomerMasterPage';
// Stage 6B.3 — Supplier Master. Tenant-schema; mirrors the customer
// shape but trimmed (no wastage, no parent-customer map, no Aadhaar).
import SupplierMasterPage from '../pages/erp/masters/suppliers/SupplierMasterPage';
// Stage 6B.8A-1 — UOM Master with Indian GST UQC mapping. Tenant-
// schema; 20 system UOMs seed automatically. Future Item Master /
// Purchase Bill / Sales Invoice consume the lookup endpoint.
import UomMasterPage from '../pages/erp/masters/uoms/UomMasterPage';
// Stage 6B.8A-2A — Item Configuration Masters (categories / material
// types / polish types). Tabbed page; feeds Product Creation Studio.
import ItemConfigPage from '../pages/erp/masters/itemConfig/ItemConfigPage';
// Stage 6B.8A-3 — Product Creation Studio: guided wizard for items
// + glass / profile / hardware / jobwork / ecommerce / image
// extensions. UI foundation only; no new backend table.
import ProductCreationStudioPage from '../pages/erp/masters/items/ProductCreationStudioPage';
// Stage 6B.8A-4 — Item Master grid (lightweight list + filter +
// quick-view). Default item list/grid does NOT join any extension
// table; the Studio handles create/edit and is reached from the
// "Create Item" / row-Edit CTAs on this page.
import ItemMasterPage from '../pages/erp/masters/items/ItemMasterPage';
import InterlayerItemsPage from '../pages/erp/masters/interlayers/InterlayerItemsPage';
import DguConsumablesPage from '../pages/erp/masters/dguConsumables/DguConsumablesPage';
// Stage 6B.8A-3I — Ready-Made Glass Concept Master + BOQ Templates.
// Gallery + table view of reusable architectural concepts that drive
// future quotation auto-fill. Reuses erp.masters.items.* perms.
import GlassConceptsPage from '../pages/erp/masters/glassConcepts/GlassConceptsPage';
// Stage 6B.8A-3K — Process / Fabrication Master (spreadsheet UI).
// Inline-editable grid for process rates + thickness-wise matrix.
// Reuses erp.masters.items.* perms.
import ProcessMasterPage from '../pages/erp/masters/processes/ProcessMasterPage';
// Stage 6B.8A-3K.1 — Thickness Master (extracted from Process Master).
// Compact master page that drives the thickness columns shown by
// Process Master and (future) Glass / Proforma / Production surfaces.
import ThicknessMasterPage from '../pages/erp/masters/thicknesses/ThicknessMasterPage';
// Stage 6B.8A-3K.3 — Production Department Master.
// Main → Sub (Manual / Machine) hierarchy. Source of truth for
// Process Master + (future) Work Order, Production, Barcode, Costing.
import ProductionDepartmentsPage from '../pages/erp/masters/productionDepartments/ProductionDepartmentsPage';
// Stage 6B.8A-3M — Glass Type + Fabrication Masters (independent
// flat masters, inline CRUD only — no drawer / no modal).
import GlassTypeMasterPage from '../pages/erp/masters/glassTypes/GlassTypeMasterPage';
// Stage WO-2A — Glass Type → Production Route config (type-driven routing).
import GlassTypeRoutingPage from '../pages/erp/masters/glassTypeRouting/GlassTypeRoutingPage';
// Stage 6CM.2 — Proforma Settings page removed from the UI (superseded by the
// Company Defaults Master). The backend table/route + proformaSettingsApi remain
// as the Company-Defaults effective-default fallback.
import ProformaGridSettingsPage from '../pages/erp/masters/proformaGridSettings/ProformaGridSettingsPage';
// Stage 6CM.1 — Company Defaults Master (central default setup values).
import CompanyDefaultsPage from '../pages/erp/companyDefaults/CompanyDefaultsPage';
import FabricationMasterPage from '../pages/erp/masters/fabrications/FabricationMasterPage';
import GlassCategoryMasterPage from '../pages/erp/masters/glassCategories/GlassCategoryMasterPage';
// Stage HRMS-1 — HRMS foundation pages.
import DepartmentsPage from '../pages/erp/hrms/departments/DepartmentsPage';
import DesignationsPage from '../pages/erp/hrms/designations/DesignationsPage';
import EmployeesPage from '../pages/erp/hrms/employees/EmployeesPage';
import SalespersonMappingPage from '../pages/erp/hrms/salespersonMapping/SalespersonMappingPage';
// Stage HRMS-2 — HR Branch master.
import BranchesPage from '../pages/erp/hrms/branches/BranchesPage';
// HRMS Hub (module landing).
import HrmsHubPage from '../pages/erp/hrms/hub/HrmsHubPage';
// Stage HRMS-3 — org chart, employee transfers, employee history.
import OrganizationChartPage from '../pages/erp/hrms/organization/OrganizationChartPage';
import EmployeeTransfersPage from '../pages/erp/hrms/transfers/EmployeeTransfersPage';
import EmployeeHistoryPage from '../pages/erp/hrms/history/EmployeeHistoryPage';
// Stage 6OC.1 — Other Charges Master (rule master + preview engine).
import OtherChargesMasterPage from '../pages/erp/otherCharges/OtherChargesMasterPage';
import ManualChargeMasterPage from '../pages/erp/masters/manualCharges/ManualChargeMasterPage';
import PriceListsPage from '../pages/erp/masters/priceLists/PriceListsPage';
import PriceListDetailPage from '../pages/erp/masters/priceLists/PriceListDetailPage';

// Stage 6Q.1 — Sales Proforma Invoice Foundation.
import ProformaListPage from '../pages/erp/sales/proforma/ProformaListPage';
import ProformaApprovalsPage from '../pages/erp/sales/proforma/ProformaApprovalsPage';
import ProformaEditorPage from '../pages/erp/sales/proforma/ProformaEditorPage';
import ChangeOrderPage from '../pages/erp/sales/proforma/ChangeOrderPage';
// Sales Tax Invoice (GST) — Phase 1B. Reuses ProformaEditorPage in invoice mode.
// Aliased to avoid the control-plane `InvoiceListPage` (subscription invoices).
import SalesTaxInvoiceListPage from '../pages/erp/sales/invoice/InvoiceListPage';

// Stage WO-1A — Work Orders (Production). Turns a CONFIRMED proforma into a
// piece-tracked, QR-barcoded work order. List + detail (lines + pieces). RBAC
// server-side via `erp.production.workorder.*`. `/work-orders` list is
// registered BEFORE the `/:id` detail so no static segment is shadowed.
import WorkOrdersPage from '../pages/erp/production/workOrders/WorkOrdersPage';
import WorkOrderDetailPage from '../pages/erp/production/workOrders/WorkOrderDetailPage';
import WorkOrderStickersPage from '../pages/erp/production/workOrders/WorkOrderStickersPage';
import WorkOrderCopyPage from '../pages/erp/production/workOrders/WorkOrderCopyPage';
import WorkOrderScanPage from '../pages/erp/production/workOrders/WorkOrderScanPage';
// Stage SCAN-2 — tenant scanner fleet: department + entry/exit mapping.
import ScanDevicesPage from '../pages/erp/production/scanDevices/ScanDevicesPage';
// Stage SCAN-6 — Ingest Monitor: what the scanner fleet sent, and the
// engine's verdict on each record.
import ScanIngestMonitorPage from '../pages/erp/production/scanIngest/ScanIngestMonitorPage';
// Internal Breakage → Rework Request — MFA-approved breakage approvals inbox.
import BreakageApprovalsPage from '../pages/erp/production/breakage/BreakageApprovalsPage';
// Stage PRODUCTION-CUTTING-PLANNER-1 — Cutting Planner & Plan Tracker.
import ProductionPlanning from '../pages/erp/production/cuttingPlanner/ProductionPlanning';
import CuttingPlanUtilizationReportPage from '../pages/erp/production/cuttingPlanner/CuttingPlanUtilizationReportPage';

// Stage 6P.1 + 6P.2 — Purchase Request + Purchase Order Placement Foundation.
import PurchaseRequestsPage    from '../pages/erp/purchase/PurchaseRequestsPage';
import PurchaseRequestFormPage from '../pages/erp/purchase/PurchaseRequestFormPage';
import RfqListPage             from '../pages/erp/rfq/RfqListPage';
import RfqDetailPage           from '../pages/erp/rfq/RfqDetailPage';
import PurchaseOrdersPage      from '../pages/erp/purchase/PurchaseOrdersPage';
import PurchaseOrderFormPage   from '../pages/erp/purchase/PurchaseOrderFormPage';

// Stage 6P.3 — GRN / Material Receipt Foundation.
import GoodsReceiptsPage       from '../pages/erp/purchase/GoodsReceiptsPage';
import GoodsReceiptFormPage    from '../pages/erp/purchase/GoodsReceiptFormPage';
import GoodsReceiptReportViewerPage from '../pages/erp/purchase/GoodsReceiptReportViewerPage';

// Stage 6P.4 — Purchase Bill Foundation + PO/GRN Matching.
import PurchaseBillsPage       from '../pages/erp/purchase/PurchaseBillsPage';
import PurchaseBillFormPage    from '../pages/erp/purchase/PurchaseBillFormPage';

// Stage 6P.5 — Inventory Inward Posting Foundation.
import InventoryDashboardPage   from '../pages/erp/inventory/InventoryDashboardPage';
import CurrentStockPage         from '../pages/erp/inventory/CurrentStockPage';
import StockLedgerPage          from '../pages/erp/inventory/StockLedgerPage';
// Stage INVENTORY-OFFCUTS-1 — Off-cut register.
import OffcutsPage              from '../pages/erp/inventory/offcuts/OffcutsPage';
import PvbInventoryPage         from '../pages/erp/inventory/interlayers/PvbInventoryPage';
import DguConsumableInventoryPage from '../pages/erp/inventory/dguConsumables/DguConsumableInventoryPage';
import DguConsumableIssuePage from '../pages/erp/inventory/dguConsumables/DguConsumableIssuePage';
import DispatchPlansPage from '../pages/erp/dispatch/DispatchPlansPage';
import DispatchPlanDetailPage from '../pages/erp/dispatch/DispatchPlanDetailPage';
import DispatchLoadingSlipPage from '../pages/erp/dispatch/DispatchLoadingSlipPage';
import DeliveryWindowPage from '../pages/erp/dispatch/DeliveryWindowPage';
import PurchaseInwardPage       from '../pages/erp/inventory/PurchaseInwardPage';
import PostInventoryInwardPage  from '../pages/erp/inventory/PostInventoryInwardPage';

// Stage 6P.6 — Purchase Bill Accounting Posting Foundation. Bridges the
// Purchase Bill module (6P.4) into the voucherPosting engine (6B.4) via
// a new tenant-schema bookkeeping layer (purchase_accounting_postings +
// posting lines + GST ITC register + posting event log). RBAC server-
// side via `erp.purchase.accounting.*` permissions.
import PurchaseAccountingPostingsPage from '../pages/erp/purchaseAccounting/PurchaseAccountingPostingsPage';
import PostPurchaseBillAccountingPage from '../pages/erp/purchaseAccounting/PostPurchaseBillAccountingPage';
import PurchaseGstItcPage             from '../pages/erp/purchaseAccounting/PurchaseGstItcPage';
import UnpostedPurchaseBillsPage      from '../pages/erp/purchaseAccounting/UnpostedPurchaseBillsPage';
import SupplierPayablesPage           from '../pages/erp/purchaseAccounting/SupplierPayablesPage';

// Stage 6P.7 — Supplier Payment + Payment Voucher Integration. New
// tenant-schema bookkeeping layer (supplier_payments + allocations +
// posting lines + payable balances + event log). Bridges Purchase Bills
// (6P.4) and Purchase Accounting (6P.6) into the voucherPosting engine
// (6B.4) via a dedicated supplier-payment surface. RBAC server-side via
// `erp.supplier_payments.*` permissions; post / cancel / delete are L1
// Approval-MFA gated at the route layer (out of scope for this stage —
// keys defined only). Lives at /erp/supplier-payments/*. The
// SupplierPayables list reuses the same surface family but lives under
// /erp/supplier-payments/payables to keep AP analytics co-located with
// the transactional payment screens; the 6P.6 read-only
// /erp/purchase-accounting/supplier-payables placeholder remains for
// the existing purchase-side roll-up.
import SupplierPaymentsPage         from '../pages/erp/supplierPayments/SupplierPaymentsPage';
import SupplierPaymentFormPage      from '../pages/erp/supplierPayments/SupplierPaymentFormPage';
import SupplierPaymentsPayablesPage from '../pages/erp/supplierPayments/SupplierPayablesPage';
import SupplierPaymentRegisterPage  from '../pages/erp/supplierPayments/SupplierPaymentRegisterPage';
import SupplierPaymentHistoryPage   from '../pages/erp/supplierPayments/SupplierPaymentHistoryPage';

// Stage 6P.8 — Purchase Return / Debit Note Foundation. Mirrors the
// 6P.7 supplier-payment surface family: list + form (new / edit /
// from-inventory-ledger / from-purchase-bill) + register + ITC
// reversals. Form page handles all four modes via a `mode` prop.
// RBAC server-side via `erp.purchase.returns.*` permissions; post /
// cancel are L1 Approval-MFA gated at the route layer.
import PurchaseReturnsPage            from '../pages/erp/purchaseReturns/PurchaseReturnsPage';
import PurchaseReturnFormPage         from '../pages/erp/purchaseReturns/PurchaseReturnFormPage';
import PurchaseReturnRegisterPage     from '../pages/erp/purchaseReturns/PurchaseReturnRegisterPage';
import PurchaseReturnItcReversalsPage from '../pages/erp/purchaseReturns/PurchaseReturnItcReversalsPage';

// Stage 6P.9 — TDS / TCS Foundation. Tax-rules + supplier tax profiles +
// deduction register + challan + reports. RBAC server-side via
// `erp.tds_tcs.*` permissions. Post / Cancel are L1 Approval-MFA gated
// at the route layer. Reports page accepts a `defaultTab` prop so the
// three /reports/* paths land on the right tab on first render.
import TdsTcsRulesPage          from '../pages/erp/tdsTcs/TdsTcsRulesPage';
import SupplierTaxProfilesPage  from '../pages/erp/tdsTcs/SupplierTaxProfilesPage';
import TdsTcsDeductionsPage     from '../pages/erp/tdsTcs/TdsTcsDeductionsPage';
import TdsTcsChallansPage       from '../pages/erp/tdsTcs/TdsTcsChallansPage';
import TdsTcsReportsPage        from '../pages/erp/tdsTcs/TdsTcsReportsPage';

// Stage 6B.1 — Accounts module foundation (Chart of Accounts, tax
// rates, fiscal periods, GST report aggregates). Foundation only:
// transactional UI lands with the posting engine in a later stage.
import AccountsHomePage from '../pages/erp/accounts/AccountsHomePage';
import LedgerAccountsPage from '../pages/erp/accounts/LedgerAccountsPage';
import TaxRatesPage from '../pages/erp/accounts/TaxRatesPage';
import AccountingPeriodsPage from '../pages/erp/accounts/AccountingPeriodsPage';
import GstReportsPage from '../pages/erp/accounts/GstReportsPage';
// Stage 6B.2 — Accounts setup, document sequences, opening balances.
import AccountsSetupPage from '../pages/erp/accounts/AccountsSetupPage';
import DocumentSequencesPage from '../pages/erp/accounts/DocumentSequencesPage';
import OpeningBalancesPage from '../pages/erp/accounts/OpeningBalancesPage';
// Stage 6B.4 — Voucher Posting Engine + Journal Voucher module.
import JournalVoucherPage from '../pages/erp/accounts/vouchers/JournalVoucherPage';
// Stage 6B.6 — Payment / Receipt Voucher module.
import PaymentVoucherPage from '../pages/erp/accounts/payments/PaymentVoucherPage';
// Stage SALES-RECEIPTS-1 — Customer Receipts & Advances (posts via the Receipt
// Voucher engine; backs the Proforma Accounts review).
import CustomerReceiptsPage from '../pages/erp/accounts/customerReceipts/CustomerReceiptsPage';
// Stage 6B.7 — Expense Entry module.
import ExpenseEntryPage from '../pages/erp/accounts/expenses/ExpenseEntryPage';
// Stage 6B.9C — Contra Voucher (cash/bank transfers; reuses the
// voucherPosting engine; L1-MFA on post/cancel/delete).
import ContraVoucherPage from '../pages/erp/accounts/contras/ContraVoucherPage';
// Stage 6B.9D — Fixed Assets Register foundation (asset list +
// categories + depreciation/disposal flows; L1-MFA on
// delete/depreciate/dispose).
import FixedAssetsPage from '../pages/erp/accounts/fixedAssets/FixedAssetsPage';
// Stage 6B.9E — Investments Register foundation (FD / mutual fund /
// shares / bonds; income / disposal / maturity flows; L1-MFA on
// delete/record_income/dispose/mature; valuation is register-only).
import InvestmentsPage from '../pages/erp/accounts/investments/InvestmentsPage';
// Stage 6B.5 — Read-only Account Reports.
import TrialBalancePage from '../pages/erp/accounts/reports/TrialBalancePage';
import LedgerStatementPage from '../pages/erp/accounts/reports/LedgerStatementPage';
import VoucherRegisterPage from '../pages/erp/accounts/reports/VoucherRegisterPage';
import AccountBalancesPage from '../pages/erp/accounts/reports/AccountBalancesPage';
// Stage 6B.7E — Profit & Loss + Balance Sheet (read-only).
import ProfitLossPage from '../pages/erp/accounts/reports/ProfitLossPage';
import BalanceSheetPage from '../pages/erp/accounts/reports/BalanceSheetPage';
// Stage 6B.9G — Fixed Asset Schedule + Investment Schedule (read-only).
import FixedAssetSchedulePage from '../pages/erp/accounts/reports/FixedAssetSchedulePage';
import InvestmentSchedulePage from '../pages/erp/accounts/reports/InvestmentSchedulePage';
// Stage 6E.3 — Debtors / Creditors Aging pages.
import DebtorsAgingPage      from '../pages/erp/accounts/reports/DebtorsAgingPage';
import CreditorsAgingPage    from '../pages/erp/accounts/reports/CreditorsAgingPage';
// Stage 6E.1 — Bank Reconciliation foundation + Cheque Tracking
// (cheque clearance workflow + monthly BRS; L1-MFA on
// delete/lock/cancel + cheque bounce/cancel/stop).
import BankReconciliationsPage from '../pages/erp/accounts/bankReconciliation/BankReconciliationsPage';
import BankReconciliationDetailPage from '../pages/erp/accounts/bankReconciliation/BankReconciliationDetailPage';
import ChequeTrackingPage from '../pages/erp/accounts/bankReconciliation/ChequeTrackingPage';
// Stage 6E.2 — Year-End Closing + Opening Balance Voucher
// (FY rollover automation + closing JV via the voucherPosting engine;
//  opening balance voucher is a snapshot only to avoid double-count).
import YearEndClosingPage from '../pages/erp/accounts/yearEndClosing/YearEndClosingPage';

// Stage R3 viewer rework — back-compat redirect from the legacy
// /reports/jobs/:id route to the new single-viewer route. Wraps a
// react-router <Navigate> so the redirect happens before mount and
// never flashes the old viewer.
function RedirectToViewer() {
  const { id } = useParams();
  if (!id) return <Navigate to="/subscription" replace />;
  return <Navigate to={`/reports/viewer/${id}`} replace />;
}

function ProtectedRoute() {
  const { status } = useAuth();
  if (status === 'unknown') return <LoadingBlock tip="Restoring session…" height={400} />;
  if (status !== 'authenticated') return <Navigate to="/login" replace />;
  return <Outlet />;
}

function PublicOnlyRoute() {
  const { status } = useAuth();
  if (status === 'unknown') return <LoadingBlock tip="Loading…" height={400} />;
  if (status === 'authenticated') return <Navigate to="/subscription" replace />;
  return <Outlet />;
}

// Non-master users (sub-users) cannot land on /subscription, so route
// them to /profile, the only universally-accessible authenticated page.
function MasterOnlyRoute() {
  const { me } = useAuth();
  if (!me) return <LoadingBlock height={400} />;
  if (me.isMaster !== true) return <Navigate to="/profile" replace />;
  return <Outlet />;
}

function NotSuspendedRoute() {
  const { me } = useAuth();
  if (!me) return <LoadingBlock height={400} />;
  const eff = me.subscription && me.subscription.effectiveStatus;
  if (eff === 'suspended') return <Navigate to="/subscription" replace />;
  return <Outlet />;
}

// Stage 5N-2B: internal-admin protected route. Reads from the
// dedicated InternalAdminAuthContext only — tenant auth state is
// irrelevant here. If the operator is not signed in, redirect to
// /internal/login (NOT /login).
function InternalAdminProtectedRoute() {
  const { status } = useInternalAdminAuth();
  if (status === 'unknown') return <LoadingBlock tip="Restoring admin session…" height={400} />;
  if (status !== 'authenticated') return <Navigate to="/internal/login" replace />;
  return <Outlet />;
}

function InternalAdminPublicOnlyRoute() {
  const { status } = useInternalAdminAuth();
  if (status === 'unknown') return <LoadingBlock tip="Loading…" height={400} />;
  if (status === 'authenticated') return <Navigate to="/internal/accounts/pending" replace />;
  return <Outlet />;
}

const router = createBrowserRouter([
  {
    element: <PublicOnlyRoute />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { path: '/login', element: <LoginPage /> },
          // Stage 5N-6: forgot/reset password lives under PublicOnlyRoute
          // so an authenticated user is bounced to /subscription (a logged-in
          // user shouldn't be on a public reset page; they should change
          // password from inside the app instead).
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password',  element: <ResetPasswordPage /> },
          { path: '/signup', element: <SignupStartPage /> },
          { path: '/signup/otp', element: <SignupOtpPage /> },
          // Deprecated route — keep mounted as a one-way redirect so
          // bookmarks resolve to the merged /signup/payment surface.
          { path: '/signup/quote', element: <Navigate to="/signup/payment" replace /> },
          // Stage 5N-12: SEZ customers wait here between OTP-verified
          // and payment-unlocked. Polls /signup/status until admin
          // approves and commits a tax decision.
          { path: '/signup/sez-pending', element: <SignupSezPendingPage /> },
          { path: '/signup/payment', element: <SignupPaymentPage /> },
          { path: '/signup/done', element: <SignupDonePage /> }
        ]
      }
    ]
  },
  // Public legal / marketing pages — accessible to anyone, logged in
  // or not. They sit OUTSIDE PublicOnlyRoute so an authenticated user
  // who follows a footer link from the dashboard isn't redirected
  // away. They use the lightweight PublicLegalPageLayout (header +
  // footer + content card) instead of the auth-shell layout.
  {
    element: <PublicLegalPageLayout />,
    children: [
      { path: '/privacy', element: <PrivacyPolicyPage /> },
      { path: '/terms', element: <TermsOfServicePage /> },
      { path: '/refund-policy', element: <RefundPolicyPage /> },
      { path: '/contact', element: <ContactPage /> }
    ]
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/subscription" replace /> },
          { path: '/profile', element: <MyProfilePage /> },
          { path: '/session', element: <SessionDebugPage /> },
          // Stage R3 viewer rework — reusable report engine. Crystal
          // Reports-style: every click creates a fresh temporary job
          // and lands the user on a single viewer route. The
          // job-history list is intentionally NOT mounted; users
          // never see a "reports list" surface. Old paths converge
          // on the new viewer (or /subscription when no jobId is
          // available), so any pre-R3 tab still resolves cleanly.
          { path: '/reports', element: <Navigate to="/subscription" replace /> },
          { path: '/reports/request', element: <Navigate to="/subscription" replace /> },
          { path: '/reports/jobs/:id', element: <RedirectToViewer /> },
          { path: '/reports/viewer/:jobId', element: <ReportEngineViewerPage /> },
          // Stage 6A foundation — sandbox preview for the reusable
          // ErpDataGrid. Authenticated-only; no Master gate so a
          // sub-user can also view the demo. Local state only.
          { path: '/dev/erp-grid-demo', element: <ErpGridDemoPage /> },
          // Stage 6A Grid Increment 3 — analytical surfaces sandbox:
          // nested AND/OR filter UI, group summaries, and the pivot
          // preview. Authenticated-only, no Master gate. Local state.
          { path: '/dev/erp-grid-bi-demo', element: <ErpGridBiDemoPage /> },
          // Stage 6A.2 — Customer Master. Tenant-schema backed; per-user
          // RBAC gate enforced server-side via `erp.masters.customers.*`
          // permissions (master/admin can write; manager/staff/viewer
          // read-only). Route is authenticated-only — no master gate —
          // so sub-users with view permission can open it.
          { path: '/erp/masters/customers', element: <CustomerMasterPage /> },
          // Stage 6B.3 — Supplier Master. RBAC server-side via
          // `erp.masters.suppliers.*` permissions; delete + non-active
          // status changes require L1 Approval MFA.
          { path: '/erp/masters/suppliers', element: <SupplierMasterPage /> },
          // Stage 6B.8A-1 — UOM Master with Indian GST UQC mapping.
          // RBAC server-side via `erp.masters.uoms.*` permissions; no
          // Approval MFA (no destructive financial impact).
          { path: '/erp/masters/uoms', element: <UomMasterPage /> },
          // Stage 6B.8A-2A — Item Configuration Masters. Single route
          // hosting three tabs (categories / material types / polish
          // types); per-resource RBAC via `erp.masters.item_*` keys.
          { path: '/erp/masters/item-config', element: <ItemConfigPage /> },
          // Stage 6B.8A-4 — Item Master grid. The main daily-driver
          // listing page. Registered BEFORE the `/studio/*` routes so
          // the literal `studio` segment doesn't get parsed as an
          // item id.
          { path: '/erp/masters/items',                 element: <ItemMasterPage /> },
          // Stage 6B.8A-3 — Product Creation Studio. Guided wizard
          // that orchestrates the slim core inventory_items row plus
          // any combination of the six extensions (glass / profile /
          // hardware / jobwork / ecommerce / images). The `new`
          // route is registered BEFORE `:id/edit` so the literal
          // "new" segment doesn't shadow the UUID-typed parameter.
          { path: '/erp/masters/items/studio/new',      element: <ProductCreationStudioPage /> },
          { path: '/erp/masters/items/studio/:id/edit', element: <ProductCreationStudioPage /> },
          // Stage INTERLAYER-STOCK-1 — Interlayer (PVB/EVA/SGP) film items.
          { path: '/erp/masters/interlayers',           element: <InterlayerItemsPage /> },
          // Stage DGU-CONSUMABLE-1 — DGU consumables (spacer/desiccant/sealants/corner-key).
          { path: '/erp/masters/dgu-consumables',        element: <DguConsumablesPage /> },
          // Stage 6B.8A-3I — Ready-Made Glass Concept Master.
          { path: '/erp/masters/glass-concepts',        element: <GlassConceptsPage /> },
          // Stage 6B.8A-3K — Process / Fabrication Master spreadsheet.
          { path: '/erp/masters/processes',             element: <ProcessMasterPage /> },
          // Stage 6B.8A-3K.1 — Thickness Master.
          { path: '/erp/masters/thicknesses',           element: <ThicknessMasterPage /> },
          // Stage 6B.8A-3K.3 — Production Department Master.
          { path: '/erp/masters/production-departments', element: <ProductionDepartmentsPage /> },
          // Stage 6B.8A-3M — Glass Type + Fabrication Masters (inline CRUD).
          { path: '/erp/masters/glass-types',           element: <GlassTypeMasterPage /> },
          // Stage WO-2A — Glass Type → Production Route config.
          { path: '/erp/masters/glass-type-routing',    element: <GlassTypeRoutingPage /> },
          { path: '/erp/masters/fabrications',          element: <FabricationMasterPage /> },
          // Stage 6Q.3 — Proforma Settings page removed in 6CM.2 (use Company Defaults).
          // Stage 6Q.4 — tenant-wide Proforma grid column visibility.
          { path: '/erp/masters/proforma-grid-settings', element: <ProformaGridSettingsPage /> },
          // Stage 6CM.1 — Company Defaults Master (central default setup values).
          { path: '/erp/masters/company-defaults',      element: <CompanyDefaultsPage /> },
          { path: '/erp/masters/glass-categories',      element: <GlassCategoryMasterPage /> },
          // HRMS Hub — module landing page.
          { path: '/erp/hrms',                          element: <HrmsHubPage /> },
          // Stage HRMS-1 — HRMS foundation (masters + salesperson mapping).
          { path: '/erp/hrms/departments',              element: <DepartmentsPage /> },
          { path: '/erp/hrms/designations',             element: <DesignationsPage /> },
          { path: '/erp/hrms/branches',                 element: <BranchesPage /> },
          { path: '/erp/hrms/employees',                element: <EmployeesPage /> },
          { path: '/erp/hrms/salesperson-mapping',      element: <SalespersonMappingPage /> },
          // Stage HRMS-3 — organization / transfers / history.
          { path: '/erp/hrms/organization-chart',       element: <OrganizationChartPage /> },
          { path: '/erp/hrms/employee-transfers',       element: <EmployeeTransfersPage /> },
          { path: '/erp/hrms/employee-history',         element: <EmployeeHistoryPage /> },
          // Stage 6OC.1 — Other Charges Master (under ERP Masters menu).
          { path: '/erp/masters/other-charges',         element: <OtherChargesMasterPage /> },
          // Stage 6Q — Manual Charge Master (document-level ad-hoc charges).
          { path: '/erp/masters/manual-charges',        element: <ManualChargeMasterPage /> },
          { path: '/erp/masters/price-lists',          element: <PriceListsPage /> },
          { path: '/erp/masters/price-lists/:id',      element: <PriceListDetailPage /> },
          // Stage 6Q.1 — Sales Proforma (list + new/edit). `/new` BEFORE `:id`.
          { path: '/erp/sales/proformas',               element: <ProformaListPage /> },
          { path: '/erp/sales/proformas/new',           element: <ProformaEditorPage /> },
          // Stage WO-3.3 — post-cut Change Order workbench (full page, was a modal).
          { path: '/erp/sales/proformas/:id/change-order', element: <ChangeOrderPage /> },
          { path: '/erp/sales/proformas/:id',           element: <ProformaEditorPage /> },
          // Stage SALES-PROFORMA-APPROVAL-1 — L1/L2/L3 approval inbox.
          { path: '/erp/sales/proforma-approvals',      element: <ProformaApprovalsPage /> },
          // Sales Tax Invoice (GST) — Phase 1B. Shared editor in invoice mode;
          // `/new` BEFORE `:id`. No post/finalise route (Phase 2).
          { path: '/erp/sales/invoices',                element: <SalesTaxInvoiceListPage /> },
          { path: '/erp/sales/invoices/new',            element: <ProformaEditorPage mode="invoice" /> },
          { path: '/erp/sales/invoices/:id',            element: <ProformaEditorPage mode="invoice" /> },
          // Stage WO-1A — Work Orders (Production). List BEFORE `/:id` detail.
          // RBAC server-side via `erp.production.workorder.*`.
          { path: '/erp/production/work-orders',            element: <WorkOrdersPage /> },
          { path: '/erp/production/scan',                   element: <WorkOrderScanPage /> },
          // Stage SCAN-2 — the master maps each Glass-i-allocated ESP32
          // scanner to a department and picks entry vs exit. Read is open to
          // every role; the PATCH route is requireMaster-gated server-side.
          { path: '/erp/production/scan-devices',           element: <ScanDevicesPage /> },
          { path: '/erp/production/scan-ingest',            element: <ScanIngestMonitorPage /> },
          { path: '/erp/production/work-orders/:id/stickers', element: <WorkOrderStickersPage /> },
          { path: '/erp/production/work-orders/:id/work-order-copy', element: <WorkOrderCopyPage /> },
          { path: '/erp/production/work-orders/:id',        element: <WorkOrderDetailPage /> },
          // Internal Breakage → Rework Request — MFA-approved breakage inbox.
          { path: '/erp/production/breakage-approvals',     element: <BreakageApprovalsPage /> },
          // Stage PRODUCTION-CUTTING-PLANNER-1 — Cutting Planner & Plan Tracker.
          { path: '/erp/production/cutting-planner',        element: <ProductionPlanning /> },
          // Cutting Planner utilization report (per plan × thickness). Literal
          // path registered before nothing param-like — safe alongside the planner.
          { path: '/erp/production/cutting-planner/utilization', element: <CuttingPlanUtilizationReportPage /> },
          // Stage DISPATCH-PLAN-1 — Dispatch planning by delivery commitment.
          // Literal list before the :id detail so it isn't shadowed.
          { path: '/erp/dispatch/plans',                   element: <DispatchPlansPage /> },
          { path: '/erp/dispatch/plans/:id',               element: <DispatchPlanDetailPage /> },
          { path: '/erp/dispatch/plans/:id/loading-slip',  element: <DispatchLoadingSlipPage /> },
          // Stage DISPATCH-WINDOW-1 — read-only Delivery Window cockpit.
          { path: '/erp/dispatch/delivery-window',         element: <DeliveryWindowPage /> },
          // Stage 6P.1 — Purchase Requests (list + new/edit form). Static
          // `/new` registered BEFORE `:id` so the literal segment doesn't
          // get parsed as an id.
          { path: '/erp/purchase/requests',             element: <PurchaseRequestsPage /> },
          { path: '/erp/purchase/requests/new',         element: <PurchaseRequestFormPage /> },
          { path: '/erp/purchase/requests/:id',         element: <PurchaseRequestFormPage /> },
          // Stage 6RFQ — Request for Quotation (list + detail). Raised from
          // an approved PR; detail drives send / quote / compare / award.
          { path: '/erp/rfq',                           element: <RfqListPage /> },
          { path: '/erp/rfq/:id',                       element: <RfqDetailPage /> },
          // Stage 6P.2 — Purchase Orders (list + new/edit form). Same
          // static-before-dynamic ordering.
          { path: '/erp/purchase/orders',               element: <PurchaseOrdersPage /> },
          { path: '/erp/purchase/orders/new',           element: <PurchaseOrderFormPage /> },
          { path: '/erp/purchase/orders/:id',           element: <PurchaseOrderFormPage /> },
          // Stage 6P.3 — Goods Receipt Notes (list + new / from-po / edit
          // form). Static segments registered BEFORE the dynamic `:id`
          // so `new` and `from-po` aren't parsed as ids.
          { path: '/erp/purchase/grns',                 element: <GoodsReceiptsPage /> },
          { path: '/erp/purchase/grns/new',             element: <GoodsReceiptFormPage mode="new" /> },
          { path: '/erp/purchase/grns/from-po/:poId',   element: <GoodsReceiptFormPage mode="from-po" /> },
          // Print viewer — registered before the dynamic :id edit route.
          { path: '/erp/purchase/grns/:id/report',      element: <GoodsReceiptReportViewerPage /> },
          { path: '/erp/purchase/grns/:id',             element: <GoodsReceiptFormPage mode="edit" /> },
          // Stage 6P.4 — Purchase Bills (list + new / from-po / from-grn /
          // edit form). Static segments registered BEFORE the dynamic
          // `:id` so `new`, `from-po`, and `from-grn` aren't parsed as ids.
          { path: '/erp/purchase/bills',                 element: <PurchaseBillsPage /> },
          { path: '/erp/purchase/bills/new',             element: <PurchaseBillFormPage mode="new" /> },
          { path: '/erp/purchase/bills/from-po/:poId',   element: <PurchaseBillFormPage mode="from-po" /> },
          { path: '/erp/purchase/bills/from-grn/:grnId', element: <PurchaseBillFormPage mode="from-grn" /> },
          { path: '/erp/purchase/bills/:id',             element: <PurchaseBillFormPage mode="edit" /> },
          // Stage 6P.5 — Inventory Inward Posting Foundation. Static
          // segments (`grn`, `bill`) registered BEFORE any future
          // dynamic siblings; the `:grnId` / `:purchaseBillId`
          // parameters live one segment deeper so there is no shadow
          // risk. RBAC server-side via `erp.inventory.*` permissions.
          { path: '/erp/inventory',                                    element: <InventoryDashboardPage /> },
          { path: '/erp/inventory/stock',                              element: <CurrentStockPage /> },
          { path: '/erp/inventory/offcuts',                            element: <OffcutsPage /> },
          { path: '/erp/inventory/pvb',                                element: <PvbInventoryPage /> },
          // Stage DGU-CONSUMABLE-2 — separate DGU-consumables inventory.
          { path: '/erp/inventory/dgu-consumables',                    element: <DguConsumableInventoryPage /> },
          // Stage DGU-CONSUMABLE-3 — manual issue of DGU consumables to production.
          { path: '/erp/inventory/dgu-consumable-issues',              element: <DguConsumableIssuePage /> },
          { path: '/erp/inventory/stock-ledger',                       element: <StockLedgerPage /> },
          { path: '/erp/inventory/purchase-inward',                    element: <PurchaseInwardPage /> },
          { path: '/erp/inventory/purchase-inward/grn/:grnId',         element: <PostInventoryInwardPage source="grn" /> },
          { path: '/erp/inventory/purchase-inward/bill/:purchaseBillId', element: <PostInventoryInwardPage source="bill" /> },
          // Stage 6P.6 — Purchase Bill Accounting Posting Foundation.
          // Static `/post/:purchaseBillId` is the create surface; the
          // postings list is the read-side; gst-itc + unposted-bills +
          // supplier-payables are supporting analytics pages. RBAC
          // server-side via `erp.purchase.accounting.*` permissions.
          { path: '/erp/purchase-accounting/postings',                   element: <PurchaseAccountingPostingsPage /> },
          { path: '/erp/purchase-accounting/post/:purchaseBillId',       element: <PostPurchaseBillAccountingPage /> },
          { path: '/erp/purchase-accounting/gst-itc',                    element: <PurchaseGstItcPage /> },
          { path: '/erp/purchase-accounting/unposted-bills',             element: <UnpostedPurchaseBillsPage /> },
          { path: '/erp/purchase-accounting/supplier-payables',          element: <SupplierPayablesPage /> },
          // Stage 6P.7 — Supplier Payment + Payment Voucher Integration.
          // Mirrors the 6P.6 mounting pattern: static literals
          // (`/payables`, `/register`, `/history/:supplierId`, `/new`)
          // are registered BEFORE the dynamic `/:id` so the literal
          // segments aren't parsed as payment ids. The same form page
          // serves create + edit modes (mode resolved from the route
          // params inside the component). RBAC server-side via
          // `erp.supplier_payments.*`.
          { path: '/erp/supplier-payments',                              element: <SupplierPaymentsPage /> },
          { path: '/erp/supplier-payments/new',                          element: <SupplierPaymentFormPage mode="new" /> },
          { path: '/erp/supplier-payments/payables',                     element: <SupplierPaymentsPayablesPage /> },
          { path: '/erp/supplier-payments/register',                     element: <SupplierPaymentRegisterPage /> },
          { path: '/erp/supplier-payments/history/:supplierId',          element: <SupplierPaymentHistoryPage /> },
          { path: '/erp/supplier-payments/:id',                          element: <SupplierPaymentFormPage mode="edit" /> },
          // Stage 6P.8 — Purchase Return / Debit Note Foundation.
          // Mirrors the 6P.7 mounting pattern: static literals
          // (`/new`, `/register`, `/itc-reversals`, `/from-inventory-ledger/:ledgerId`,
          // `/from-purchase-bill/:purchaseBillId`) are registered BEFORE
          // the dynamic `/:id` so the literal segments aren't parsed as
          // purchase-return ids. The same form page serves create / edit /
          // from-inventory-ledger / from-purchase-bill modes via a `mode`
          // prop. RBAC server-side via `erp.purchase.returns.*`.
          { path: '/erp/purchase-returns',                                              element: <PurchaseReturnsPage /> },
          { path: '/erp/purchase-returns/new',                                          element: <PurchaseReturnFormPage mode="new" /> },
          { path: '/erp/purchase-returns/register',                                     element: <PurchaseReturnRegisterPage /> },
          { path: '/erp/purchase-returns/itc-reversals',                                element: <PurchaseReturnItcReversalsPage /> },
          { path: '/erp/purchase-returns/from-inventory-ledger/:ledgerId',              element: <PurchaseReturnFormPage mode="from-inventory-ledger" /> },
          { path: '/erp/purchase-returns/from-purchase-bill/:purchaseBillId',           element: <PurchaseReturnFormPage mode="from-purchase-bill" /> },
          { path: '/erp/purchase-returns/:id',                                          element: <PurchaseReturnFormPage mode="edit" /> },
          // Stage 6P.9 — TDS / TCS Foundation. Literal paths first; the
          // three /reports/* surfaces share TdsTcsReportsPage with a
          // `defaultTab` prop so each path lands on its tab on mount.
          // RBAC server-side via `erp.tds_tcs.*`; cancel / mark-paid
          // gated by L1 Approval MFA inside their service layer.
          { path: '/erp/tds-tcs/rules',                     element: <TdsTcsRulesPage /> },
          { path: '/erp/tds-tcs/supplier-profiles',         element: <SupplierTaxProfilesPage /> },
          { path: '/erp/tds-tcs/deductions',                element: <TdsTcsDeductionsPage /> },
          { path: '/erp/tds-tcs/challans',                  element: <TdsTcsChallansPage /> },
          { path: '/erp/tds-tcs/reports/supplier-summary',  element: <TdsTcsReportsPage defaultTab="supplier" /> },
          { path: '/erp/tds-tcs/reports/section-summary',   element: <TdsTcsReportsPage defaultTab="section" /> },
          { path: '/erp/tds-tcs/reports/pending-challans',  element: <TdsTcsReportsPage defaultTab="pending" /> },
          // Stage 6B.1 — Accounts module foundation. All sub-routes are
          // RBAC-gated server-side via `erp.accounts.*` permissions.
          { path: '/erp/accounts',                    element: <AccountsHomePage /> },
          { path: '/erp/accounts/ledgers',            element: <LedgerAccountsPage /> },
          { path: '/erp/accounts/taxes',              element: <TaxRatesPage /> },
          { path: '/erp/accounts/periods',            element: <AccountingPeriodsPage /> },
          { path: '/erp/accounts/gst-reports',        element: <GstReportsPage /> },
          // Stage 6B.2 — Accounts setup surfaces. RBAC gated via
          // `erp.accounts.setup.*` permissions.
          { path: '/erp/accounts/setup',              element: <AccountsSetupPage /> },
          { path: '/erp/accounts/document-sequences', element: <DocumentSequencesPage /> },
          { path: '/erp/accounts/opening-balances',   element: <OpeningBalancesPage /> },
          // Stage 6B.4 — Journal Voucher list + Add/Edit/View modal +
          // Approval-MFA gated Post / Cancel. RBAC server-side via
          // `erp.accounts.vouchers.*` permissions.
          { path: '/erp/accounts/vouchers',           element: <JournalVoucherPage /> },
          // Stage 6B.6 — Payment / Receipt vouchers (supplier payments
          // + customer receipts in one grid). Post / Cancel are L1
          // Approval-MFA gated server-side (op key resolved by
          // payment_type, so the audit row + MFA dialog wording match
          // the actual operation).
          { path: '/erp/accounts/payments',           element: <PaymentVoucherPage /> },
          // Stage SALES-RECEIPTS-1 — Customer Receipts (posts via the Receipt
          // Voucher engine; view/create/post gated server-side).
          { path: '/erp/accounts/customer-receipts',  element: <CustomerReceiptsPage /> },
          // Stage 6B.7 — Expense Entry. Posts via the same posting
          // engine. L1 Approval-MFA gates post/cancel server-side.
          { path: '/erp/accounts/expenses',           element: <ExpenseEntryPage /> },
          // Stage 6B.9C — Contra Voucher (cash/bank transfers).
          // Reuses voucherPosting engine. L1 Approval-MFA gates
          // post / cancel / delete server-side.
          { path: '/erp/accounts/contras',            element: <ContraVoucherPage /> },
          // Stage 6B.9D — Fixed Assets Register foundation. Depreciation
          // + disposal both delegate to the engine. L1 Approval-MFA
          // gates delete / depreciate / dispose server-side.
          { path: '/erp/accounts/fixed-assets',       element: <FixedAssetsPage /> },
          // Stage 6B.9E — Investments Register foundation. Income +
          // disposal + maturity all delegate to the engine. L1
          // Approval-MFA gates delete / record_income / dispose /
          // mature server-side. Valuation is register-only.
          { path: '/erp/accounts/investments',        element: <InvestmentsPage /> },
          // Stage 6E.1 — Bank Reconciliation foundation + Cheque
          // Tracking. L1 Approval-MFA gates delete / lock / cancel on
          // the reconciliation and bounce / cancel / stop on the
          // cheque workflow. Match / unmatch in draft is unprotected.
          { path: '/erp/accounts/bank-reconciliations',     element: <BankReconciliationsPage /> },
          { path: '/erp/accounts/bank-reconciliations/:id', element: <BankReconciliationDetailPage /> },
          { path: '/erp/accounts/cheques',                  element: <ChequeTrackingPage /> },
          // Stage 6E.2 — Year-End Closing + Opening Balance Voucher.
          // L1 Approval-MFA gates delete / post / opening-voucher /
          // reopen at the route layer. Preview is unprotected (safe
          // to regenerate, no GL effect).
          { path: '/erp/accounts/year-end-closing',         element: <YearEndClosingPage /> },
          // Stage 6B.5 — Read-only account reports. All four gated
          // server-side by `erp.accounts.reports.view`.
          { path: '/erp/accounts/reports/trial-balance',    element: <TrialBalancePage /> },
          { path: '/erp/accounts/reports/ledger-statement', element: <LedgerStatementPage /> },
          { path: '/erp/accounts/reports/voucher-register', element: <VoucherRegisterPage /> },
          { path: '/erp/accounts/reports/account-balances', element: <AccountBalancesPage /> },
          // Stage 6B.7E — Profit & Loss + Balance Sheet.
          { path: '/erp/accounts/reports/profit-loss',      element: <ProfitLossPage /> },
          { path: '/erp/accounts/reports/balance-sheet',    element: <BalanceSheetPage /> },
          // Stage 6B.9G — Fixed Asset Schedule + Investment Schedule.
          { path: '/erp/accounts/reports/fixed-asset-schedule', element: <FixedAssetSchedulePage /> },
          { path: '/erp/accounts/reports/investment-schedule',  element: <InvestmentSchedulePage /> },
          // Stage 6E.3 — Debtors / Creditors Aging.
          { path: '/erp/accounts/reports/debtors-aging',        element: <DebtorsAgingPage /> },
          { path: '/erp/accounts/reports/creditors-aging',      element: <CreditorsAgingPage /> },
          {
            element: <MasterOnlyRoute />,
            children: [
              { path: '/subscription', element: <SubscriptionPage /> },
              // Stage 5N-8: renewal page must be reachable when the
              // subscription is suspended/expired, so it sits under
              // MasterOnlyRoute but DELIBERATELY outside any
              // NotSuspendedRoute wrapper. The backend route group
              // /api/subscription/* also skips subscriptionEnforce so
              // the master can pay even after the plan lapsed.
              { path: '/subscription/renew', element: <RenewSubscriptionPage /> },
              { path: '/invoices', element: <InvoiceListPage /> },
              { path: '/invoices/:id', element: <InvoiceDetailPage /> },
              // Stage R3 follow-up: tenant invoice reports are now
              // viewed via the reusable report engine — see
              // /reports/jobs/:jobId. The legacy /invoices/:id/report
              // route + InvoiceReportViewerPage have been removed.
              {
                element: <NotSuspendedRoute />,
                children: [{ path: '/users', element: <UserListPage /> }]
              }
            ]
          }
        ]
      }
    ]
  },
  // ---------------------------------------------------------------
  // Stage 5N-2B — Glass-i Internal Admin
  // Top-level branch; does NOT share router children with the tenant
  // tree. /internal/login is gated by InternalAdminPublicOnlyRoute so
  // an authenticated admin is bounced to /internal/accounts/pending.
  // The protected branch redirects to /internal/login (NOT /login) on
  // unauth, so an operator never lands on the customer login page by
  // accident.
  {
    element: <InternalAdminPublicOnlyRoute />,
    children: [
      { path: '/internal/login', element: <InternalAdminLoginPage /> }
    ]
  },
  {
    element: <InternalAdminProtectedRoute />,
    children: [
      {
        element: <InternalAdminLayout />,
        children: [
          { path: '/internal', element: <Navigate to="/internal/accounts/pending" replace /> },
          { path: '/internal/accounts', element: <Navigate to="/internal/accounts/pending" replace /> },
          { path: '/internal/accounts/pending',   element: <PendingAccountsPage /> },
          { path: '/internal/accounts/approved',  element: <ApprovedAccountsPage /> },
          { path: '/internal/accounts/rejected',  element: <RejectedAccountsPage /> },
          { path: '/internal/accounts/suspended', element: <SuspendedAccountsPage /> },
          // Stage 5N-12: SEZ pending-signup admin queue. SEZ
          // customers (sez_unit / sez_developer) wait here for the
          // admin to commit a tax decision before payment opens.
          { path: '/internal/sez-pending', element: <SezPendingSignupsPage /> },
          { path: '/internal/audit',              element: <AuditLogsPage /> },
          { path: '/internal/profile',            element: <AdminProfilePage /> },
          // Stage 5N-3 Custom Plan management. Static routes (/new)
          // are listed BEFORE the :id dynamic route so a literal
          // 'new' segment doesn't fall into the parameterised match.
          { path: '/internal/custom-plans',           element: <CustomPlansListPage /> },
          { path: '/internal/custom-plans/new',       element: <CustomPlanFormPage mode="create" /> },
          { path: '/internal/custom-plans/:id/edit',  element: <CustomPlanFormPage mode="edit" /> },
          { path: '/internal/custom-plans/:id',       element: <CustomPlanDetailPage /> },
          // Stage 5N-4 Pricing & Tax master.
          { path: '/internal/pricing',                element: <PricingPage /> },
          // Stage R2 Report engine catalog.
          { path: '/internal/reports/definitions',    element: <ReportDefinitionsPage /> },
          // Stage 5N-CLONE Tenant configuration clone (super_admin only).
          { path: '/internal/tenant-clone',           element: <TenantConfigClonePage /> },
          // Stage SCAN-1 ESP32 scanner fleet: hardware inventory + tenant
          // allocation. Department assignment is deliberately NOT here — that
          // belongs to the tenant's master user on their own screen.
          { path: '/internal/scan-devices',           element: <ScanDeviceInventoryPage /> },
          // tenant detail must come AFTER the static-named routes so
          // 'pending'/'approved'/'rejected'/'suspended' don't fall
          // into the :tenantId pattern.
          { path: '/internal/accounts/:tenantId', element: <AccountDetailPage /> },
          // Stage 5N-9A — internal-admin Jasper report viewer.
          { path: '/internal/invoices/:invoiceId/report',
            element: <InternalInvoiceReportViewerPage /> }
        ]
      }
    ]
  },
  { path: '*', element: <Navigate to="/subscription" replace /> }
]);

export default router;
